/**
 * Makes working a yard something you do, not something you watch.
 *
 * V1 filled a bar while the player stood still. V1.5 walked the avatar along a
 * mowing path automatically, which looked right but was still a cutscene: the
 * player's hands were off the controls for ninety in-game minutes.
 *
 * Then the yard became a grid of patches the player mows themselves. Walking
 * over an uncut patch with the mower running cuts it, permanently and visibly,
 * and how much of the lawn they actually cut sets the quality of the job - and
 * so the tip, the reputation gain and whether the customer comes back.
 *
 * V3 adds the other two thirds of the trade. A yard now has, depending on what
 * the player owns and what the property needs:
 *
 * - **grass** to mow, as before;
 * - **edges** to trim, along the boundary, once there is a trimmer in the kit;
 * - **debris** to clear, once there is a blower, on a big or neglected lot.
 *
 * There is no tool-switch button. Which tool is in hand is decided by what the
 * player is standing next to - edging near the boundary, debris on top of a
 * pile, grass otherwise. A key to cycle tools would be a fourth thing to teach
 * and would not exist at all on a phone, and the yard already says
 * unambiguously what needs doing where.
 *
 * Cost: the cut grass is one mesh per property with a canvas alpha map, so
 * painting a patch is a rectangle fill rather than new geometry. Edges and
 * debris are real meshes, but only the yard being worked has them - they are
 * built on first visit and hidden with the overlay.
 */
import * as THREE from 'three';
import { getLocation } from '../../sim/data/world.js';

const CLIPPING_COUNT = 48;
/** Patches across the yard in each direction. */
const GRID = 14;
/** Canvas pixels per patch. Small: this is a mask, not a detail texture. */
const CELL_PX = 8;
/** Overgrown edging segments around the boundary of a yard. */
const EDGE_SEGMENTS = 20;
/** Piles of clippings and leaves to clear. */
const DEBRIS_PILES = 7;
/** How close the player must be to work an edge or a pile, in metres. */
const EDGE_REACH = 1.5;
const DEBRIS_REACH = 1.6;
/** Inside this distance of the boundary, the trimmer is the tool in hand. */
const EDGE_BAND = 2.4;

/** Which tool the player is holding, decided by where they are standing. */
export type YardTool = 'mow' | 'trim' | 'clear';

interface Yard {
  overlay: THREE.Mesh;
  canvas: HTMLCanvasElement;
  context: CanvasRenderingContext2D;
  texture: THREE.CanvasTexture;
  /** Per-patch cut state, row-major. */
  cut: Uint8Array;
  /** Patches under the house itself, which no mower can reach. */
  blocked: Uint8Array;
  cutCount: number;
  mowable: number;
  width: number;
  depth: number;
  centreX: number;
  centreZ: number;
  /** Ragged edging around the boundary. Trimmed segments are hidden. */
  edges: THREE.Mesh[];
  edgesDone: number;
  /** Heaps of clippings and leaves. Cleared piles are hidden. */
  debris: THREE.Mesh[];
  debrisDone: number;
}

export class YardWorkView {
  private yards = new Map<string, Yard>();
  private clippings: THREE.Points;
  private clippingVelocities: Float32Array;
  private clippingLife: Float32Array;
  private active: Yard | null = null;
  private cuttingThisFrame = false;
  private activeTool: YardTool = 'mow';
  private hasEdges = false;
  private hasDebris = false;
  // Shared across every yard: the geometry is identical and only the transform
  // differs, so this is meshes-per-yard rather than materials-per-yard.
  private readonly edgeGeometryX = new THREE.BoxGeometry(1, 0.3, 0.28);
  private readonly edgeGeometryZ = new THREE.BoxGeometry(0.28, 0.3, 1);
  private readonly edgeMaterial = new THREE.MeshStandardMaterial({
    // Yellower and drier than the lawn: grass the mower could not reach.
    color: 0x9bad52, roughness: 1,
  });
  private readonly debrisGeometry = new THREE.ConeGeometry(0.52, 0.24, 7);
  private readonly debrisMaterial = new THREE.MeshStandardMaterial({
    color: 0x8a7a44, roughness: 1,
  });

  constructor(private readonly group: THREE.Group) {
    const positions = new Float32Array(CLIPPING_COUNT * 3);
    this.clippingVelocities = new Float32Array(CLIPPING_COUNT * 3);
    this.clippingLife = new Float32Array(CLIPPING_COUNT);
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    this.clippings = new THREE.Points(
      geometry,
      new THREE.PointsMaterial({
        color: 0x8fd45a, size: 0.12, transparent: true, opacity: 0.9,
        depthWrite: false, sizeAttenuation: true,
      }),
    );
    this.clippings.frustumCulled = false;
    this.clippings.visible = false;
    group.add(this.clippings);
  }

  /** Fraction of the active yard that has been cut, 0..1. */
  get coverage(): number {
    if (!this.active) return 0;
    return this.active.mowable === 0 ? 1 : this.active.cutCount / this.active.mowable;
  }

  /** Fraction of the boundary that has been trimmed, 0..1. */
  get edgeProgress(): number {
    if (!this.active || this.active.edges.length === 0) return 0;
    return this.active.edgesDone / this.active.edges.length;
  }

  /** Fraction of the debris that has been cleared, 0..1. */
  get clearProgress(): number {
    if (!this.active || this.active.debris.length === 0) return 0;
    return this.active.debrisDone / this.active.debris.length;
  }

  /** True while the player is actually working. Drives sound. */
  get cutting(): boolean {
    return this.cuttingThisFrame;
  }

  /** Which tool the player has in hand, given where they are standing. */
  get tool(): YardTool {
    return this.activeTool;
  }

  /**
   * Begin a job on a property.
   *
   * `tasks` says which parts of the job exist, which the simulation decides
   * from the player's kit. Anything not in the list is hidden: a player with
   * no trimmer should see a tidy boundary, not a ragged one they are unable
   * to do anything about.
   */
  begin(locationId: string, tasks: readonly YardTool[] = ['mow']): void {
    const yard = this.yardFor(locationId);
    this.active = yard;
    yard.overlay.visible = true;
    const wantEdges = tasks.includes('trim');
    const wantDebris = tasks.includes('clear');
    for (let i = 0; i < yard.edges.length; i++) {
      yard.edges[i]!.visible = wantEdges && i >= yard.edgesDone;
    }
    for (let i = 0; i < yard.debris.length; i++) {
      yard.debris[i]!.visible = wantDebris && i >= yard.debrisDone;
    }
    this.hasEdges = wantEdges;
    this.hasDebris = wantDebris;
  }

  end(): void {
    if (this.active) {
      for (const edge of this.active.edges) edge.visible = false;
      for (const pile of this.active.debris) pile.visible = false;
    }
    this.active = null;
    this.cuttingThisFrame = false;
    this.activeTool = 'mow';
    this.hasEdges = false;
    this.hasDebris = false;
    this.clippings.visible = false;
  }

  /**
   * Do whatever the player is standing over.
   *
   * The tool is chosen here rather than by the player, from proximity: within
   * `EDGE_BAND` of the boundary the trimmer is out, on top of a pile the
   * blower is, otherwise the mower. Returns true if that actually changed
   * something, so the caller can decide whether to make a noise about it.
   */
  workAt(x: number, z: number): boolean {
    const yard = this.active;
    if (!yard) return false;
    let did = false;

    // Debris first: a pile can sit anywhere, including in the edge band, and
    // standing on one and being told you are trimming would be nonsense.
    let cleared = false;
    if (this.hasDebris) {
      const pile = this.nearestUndone(yard.debris, yard.debrisDone, x, z, DEBRIS_REACH);
      if (pile >= 0) {
        this.swapDown(yard.debris, pile, yard.debrisDone);
        yard.debris[yard.debrisDone]!.visible = false;
        yard.debrisDone += 1;
        cleared = true;
        did = true;
      }
    }

    const nearBoundary = this.hasEdges && (
      Math.abs(Math.abs(x - yard.centreX) - yard.width / 2) < EDGE_BAND
      || Math.abs(Math.abs(z - yard.centreZ) - yard.depth / 2) < EDGE_BAND
    );
    if (nearBoundary) {
      const edge = this.nearestUndone(yard.edges, yard.edgesDone, x, z, EDGE_REACH);
      if (edge >= 0) {
        this.swapDown(yard.edges, edge, yard.edgesDone);
        yard.edges[yard.edgesDone]!.visible = false;
        yard.edgesDone += 1;
        did = true;
      }
    }

    // The mower always passes, whatever else is happening.
    //
    // This is not a detail. An earlier version returned as soon as it had
    // trimmed, which meant the grass under the edge band could never be cut -
    // so buying a trimmer made a 100% mow permanently unreachable, and the
    // player was worse off for owning one. That is precisely the failure the
    // whole design forbids (ADR-0016): a tool must add work, never subtract
    // credit. A smoke check found it, which is why the mowing sweep in that
    // check deliberately stays off the boundary.
    if (this.cutAt(x, z)) did = true;

    this.activeTool = cleared ? 'clear' : nearBoundary ? 'trim' : 'mow';
    return did;
  }

  /**
   * Index of the nearest not-yet-done item within reach, or -1.
   *
   * Done items are kept at the front of the array and skipped, which avoids a
   * parallel "done" flag per item and keeps the counts and the array in step
   * by construction.
   */
  private nearestUndone(
    items: THREE.Mesh[], from: number, x: number, z: number, reach: number,
  ): number {
    let best = -1;
    let bestDistance = reach;
    for (let i = from; i < items.length; i++) {
      const p = items[i]!.position;
      const distance = Math.hypot(p.x - x, p.z - z);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = i;
      }
    }
    return best;
  }

  private swapDown(items: THREE.Mesh[], index: number, slot: number): void {
    const held = items[index]!;
    items[index] = items[slot]!;
    items[slot] = held;
  }

  /**
   * Cut whatever patch the mower is over.
   *
   * Returns true if this actually cut new grass, so the caller can decide
   * whether to make a noise about it.
   */
  private cutAt(x: number, z: number): boolean {
    const yard = this.active;
    if (!yard) return false;

    const u = (x - yard.centreX) / yard.width + 0.5;
    const v = (z - yard.centreZ) / yard.depth + 0.5;
    if (u < 0 || u > 1 || v < 0 || v > 1) return false;

    // The deck is wider than a point, so cut the patch under the mower and its
    // immediate neighbours. Without this, mowing feels like tracing a line.
    const cx = Math.floor(u * GRID);
    const cz = Math.floor(v * GRID);
    let cutSomething = false;
    for (let dz = -1; dz <= 1; dz++) {
      for (let dx = -1; dx <= 1; dx++) {
        // The corners of the 3x3 are outside the deck; a plus shape reads as a
        // mower width rather than a square stamp.
        if (Math.abs(dx) + Math.abs(dz) > 1) continue;
        const gx = cx + dx;
        const gz = cz + dz;
        if (gx < 0 || gx >= GRID || gz < 0 || gz >= GRID) continue;
        const index = gz * GRID + gx;
        if (yard.cut[index] || yard.blocked[index]) continue;
        yard.cut[index] = 1;
        yard.cutCount += 1;
        cutSomething = true;
        // Alternate rows are painted a shade lighter, which is what gives a
        // mown lawn its stripes.
        yard.context.fillStyle = gz % 2 === 0 ? '#ffffff' : '#d2d2d2';
        yard.context.fillRect(gx * CELL_PX, gz * CELL_PX, CELL_PX, CELL_PX);
      }
    }
    if (cutSomething) yard.texture.needsUpdate = true;
    return cutSomething;
  }

  /** Spray clippings and age the ones in flight. */
  update(dt: number, avatar: THREE.Vector3 | null, cutting: boolean): void {
    this.cuttingThisFrame = cutting;
    const positions = this.clippings.geometry.attributes.position as THREE.BufferAttribute;
    const array = positions.array as Float32Array;
    let anyAlive = false;
    for (let i = 0; i < CLIPPING_COUNT; i++) {
      const i3 = i * 3;
      if (this.clippingLife[i]! > 0) {
        this.clippingLife[i]! -= dt;
        this.clippingVelocities[i3 + 1]! -= 9.8 * dt;
        array[i3]! += this.clippingVelocities[i3]! * dt;
        array[i3 + 1]! += this.clippingVelocities[i3 + 1]! * dt;
        array[i3 + 2]! += this.clippingVelocities[i3 + 2]! * dt;
        if (array[i3 + 1]! < 0.05) this.clippingLife[i] = 0;
        else anyAlive = true;
      } else if (cutting && avatar && Math.random() < dt * 26) {
        // Spawn from behind the mower deck.
        array[i3] = avatar.x + (Math.random() - 0.5) * 0.5;
        array[i3 + 1] = 0.35;
        array[i3 + 2] = avatar.z + (Math.random() - 0.5) * 0.5;
        this.clippingVelocities[i3] = (Math.random() - 0.5) * 2.6;
        this.clippingVelocities[i3 + 1] = 1.6 + Math.random() * 1.8;
        this.clippingVelocities[i3 + 2] = (Math.random() - 0.5) * 2.6;
        this.clippingLife[i] = 0.7 + Math.random() * 0.5;
        anyAlive = true;
      } else {
        // Park unused particles far below the world rather than at the origin.
        array[i3 + 1] = -50;
      }
    }
    positions.needsUpdate = true;
    this.clippings.visible = anyAlive;
  }

  /**
   * Grass grows back. Called at each day boundary so a lawn you cut on Monday
   * is worth cutting again later - the property remembers, but not forever.
   */
  regrow(fraction = 0.34): void {
    for (const yard of this.yards.values()) {
      // Edges creep back at the same rate as the lawn, and a new day brings
      // new leaves. Without this a second visit to the same property would
      // report the edging and the clearing as already done and hand out the
      // credit for free - which would land hardest on exactly the repeat
      // customers the relationship system is built to produce.
      yard.edgesDone = Math.floor(yard.edgesDone * (1 - fraction));
      yard.debrisDone = 0;
      if (yard.cutCount === 0) continue;
      const target = Math.max(0, Math.floor(yard.cutCount * (1 - fraction)));
      // Clear patches at pseudo-random rather than in reading order, so regrowth
      // looks like grass rather than like a wipe transition.
      let index = (yard.cutCount * 7919) % (GRID * GRID);
      let guard = 0;
      while (yard.cutCount > target && guard++ < GRID * GRID * 4) {
        index = (index + 37) % (GRID * GRID);
        if (!yard.cut[index] || yard.blocked[index]) continue;
        yard.cut[index] = 0;
        yard.cutCount -= 1;
        const gx = index % GRID;
        const gz = Math.floor(index / GRID);
        yard.context.clearRect(gx * CELL_PX, gz * CELL_PX, CELL_PX, CELL_PX);
      }
      yard.texture.needsUpdate = true;
      yard.overlay.visible = yard.cutCount > 0;
    }
  }

  private yardFor(locationId: string): Yard {
    const existing = this.yards.get(locationId);
    if (existing) return existing;

    const location = getLocation(locationId);
    const width = location.width + 11;
    const depth = location.depth + 11;

    const canvas = document.createElement('canvas');
    canvas.width = GRID * CELL_PX;
    canvas.height = GRID * CELL_PX;
    const context = canvas.getContext('2d')!;
    const texture = new THREE.CanvasTexture(canvas);
    // Nearest filtering keeps each patch a crisp rectangle, which reads as mower
    // passes; linear filtering turns them into a smudge.
    texture.magFilter = THREE.NearestFilter;
    texture.minFilter = THREE.NearestFilter;

    const overlay = new THREE.Mesh(
      new THREE.PlaneGeometry(width, depth),
      new THREE.MeshStandardMaterial({
        // A distinctly fresher, lighter green than the uncut lawn beneath.
        color: 0x6fae4a, roughness: 1, transparent: true, alphaMap: texture,
      }),
    );
    overlay.rotation.x = -Math.PI / 2;
    overlay.position.set(location.x, 0.075, location.z);
    overlay.receiveShadow = true;
    overlay.renderOrder = 2;
    overlay.visible = false;
    this.group.add(overlay);

    // The house stands in the middle of the mowable rectangle. Without marking
    // its footprint unreachable, a player who mows every blade of grass they
    // can reach still tops out around 60% and is told they did a bad job.
    const blocked = new Uint8Array(GRID * GRID);
    let mowable = 0;
    for (let gz = 0; gz < GRID; gz++) {
      for (let gx = 0; gx < GRID; gx++) {
        const px = location.x + ((gx + 0.5) / GRID - 0.5) * width;
        const pz = location.z + ((gz + 0.5) / GRID - 0.5) * depth;
        const inside =
          Math.abs(px - location.x) < location.width / 2 + 0.6 &&
          Math.abs(pz - location.z) < location.depth / 2 + 0.6;
        if (inside) blocked[gz * GRID + gx] = 1;
        else mowable++;
      }
    }

    const yard: Yard = {
      overlay, canvas, context, texture,
      cut: new Uint8Array(GRID * GRID),
      blocked,
      cutCount: 0,
      mowable,
      width, depth,
      centreX: location.x,
      centreZ: location.z,
      edges: this.buildEdges(location.x, location.z, width, depth),
      edgesDone: 0,
      debris: this.buildDebris(location.x, location.z, width, depth, location.id),
      debrisDone: 0,
    };
    this.yards.set(locationId, yard);
    return yard;
  }

  /**
   * Ragged edging around the boundary of the lawn.
   *
   * Slightly taller and yellower than the lawn, and set just inside the
   * boundary so it reads as grass that has escaped the mower rather than as a
   * fence. Two shared materials across every yard, so the cost is meshes
   * rather than draw-call-per-material - and only the yard being worked has
   * any of them visible.
   */
  private buildEdges(
    centreX: number, centreZ: number, width: number, depth: number,
  ): THREE.Mesh[] {
    const meshes: THREE.Mesh[] = [];
    const perSide = Math.max(2, Math.round(EDGE_SEGMENTS / 4));
    const inset = 0.35;
    const sides: [number, number, number, number][] = [
      // x0, z0, dx, dz - each a run along one side of the boundary.
      [-width / 2 + inset, -depth / 2 + inset, 1, 0],
      [-width / 2 + inset, depth / 2 - inset, 1, 0],
      [-width / 2 + inset, -depth / 2 + inset, 0, 1],
      [width / 2 - inset, -depth / 2 + inset, 0, 1],
    ];
    for (const [x0, z0, dx, dz] of sides) {
      const run = dx ? width - inset * 2 : depth - inset * 2;
      const step = run / perSide;
      for (let i = 0; i < perSide; i++) {
        const along = (i + 0.5) * step;
        const mesh = new THREE.Mesh(
          dx ? this.edgeGeometryX : this.edgeGeometryZ,
          this.edgeMaterial,
        );
        mesh.scale.set(dx ? step / 2 : 1, 1, dz ? step / 2 : 1);
        mesh.position.set(
          centreX + x0 + dx * along,
          0.16,
          centreZ + z0 + dz * along,
        );
        mesh.castShadow = false;
        mesh.visible = false;
        this.group.add(mesh);
        meshes.push(mesh);
      }
    }
    return meshes;
  }

  /**
   * Heaps of clippings and leaves, scattered on the lawn.
   *
   * Placed from a hash of the location id rather than `Math.random`, so the
   * same yard has its piles in the same places every time it is visited. A
   * pile that moved between visits would make a half-finished job impossible
   * to resume.
   */
  private buildDebris(
    centreX: number, centreZ: number, width: number, depth: number, id: string,
  ): THREE.Mesh[] {
    let h = 2166136261;
    for (let i = 0; i < id.length; i++) {
      h ^= id.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    const next = (): number => {
      h = Math.imul(h ^ (h >>> 15), 2246822507);
      h = Math.imul(h ^ (h >>> 13), 3266489909);
      return ((h ^= h >>> 16) >>> 0) / 4294967296;
    };

    const meshes: THREE.Mesh[] = [];
    for (let i = 0; i < DEBRIS_PILES; i++) {
      // Out on the grass, not against the house: a pile you cannot walk onto
      // is a pile you cannot clear.
      const angle = next() * Math.PI * 2;
      const radius = 0.62 + next() * 0.3;
      const mesh = new THREE.Mesh(this.debrisGeometry, this.debrisMaterial);
      mesh.position.set(
        centreX + Math.cos(angle) * radius * (width / 2),
        0.11,
        centreZ + Math.sin(angle) * radius * (depth / 2),
      );
      mesh.rotation.y = next() * Math.PI;
      mesh.scale.setScalar(0.8 + next() * 0.5);
      mesh.visible = false;
      this.group.add(mesh);
      meshes.push(mesh);
    }
    return meshes;
  }
}
